import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import BoulevardBooking from "../components/BoulevardBooking";
import { Calendar, ExternalLink, Sparkles, Clock, MapPin } from "lucide-react";

const BookingContext = createContext(null);

export function BookingProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [preselect, setPreselect] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const openBooking = (serviceName = null, contact = null) => {
    setPreselect(serviceName);
    setPrefill(contact);
    setOpen(true);
  };

  const bookingUrl = config?.booking_url || "https://dashboard.boulevard.io/book";
  const embedded = config?.booking_mode === "embedded";
  const blvdApiKey = config?.blvd_api_key;
  const blvdBusinessId = config?.blvd_business_id;

  return (
    <BookingContext.Provider value={{ openBooking, config }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className={
            embedded
              ? "sm:max-w-lg rounded-2xl border-none p-0 overflow-hidden"
              : "sm:max-w-md rounded-2xl border-none p-0 overflow-hidden"
          }
          data-testid="booking-dialog"
        >
          {embedded && blvdApiKey && blvdBusinessId ? (
            <>
              <DialogHeader className="sr-only">
                <DialogTitle>Book your appointment</DialogTitle>
                <DialogDescription>Real-time availability powered by Boulevard.</DialogDescription>
              </DialogHeader>
              <div className="iri-gradient h-16 flex items-center justify-center">
                <Calendar className="h-7 w-7 text-ink/70" strokeWidth={1.5} />
              </div>
              <BoulevardBooking
                key={open ? "open" : "closed"}
                apiKey={blvdApiKey}
                businessId={blvdBusinessId}
                env={config?.blvd_env}
                preselect={preselect}
                prefill={prefill}
                onClose={() => setOpen(false)}
              />
            </>
          ) : (
            <>
          <div className="iri-gradient h-24 flex items-center justify-center">
            <Calendar className="h-9 w-9 text-ink/70" strokeWidth={1.5} />
          </div>
          <div className="p-8">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-light tracking-tight text-ink">
                Book your appointment
              </DialogTitle>
              <DialogDescription className="text-[#5C6664] mt-1">
                {preselect ? (
                  <>You're booking <span className="text-ink font-medium">{preselect}</span>. </>
                ) : null}
                Real-time availability powered by Boulevard.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-5 space-y-3 text-sm text-[#5C6664]">
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-iri-mid" /> Clinton Township, Michigan
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-iri-mid" /> Tue–Sat · from 10am (closed Sun &amp; Mon)
              </div>
              <div className="flex items-center gap-3">
                <Sparkles className="h-4 w-4 text-iri-mid" /> Choose service, provider &amp; time
              </div>
            </div>

            <p className="mt-5 text-xs leading-relaxed text-[#8A9492] bg-stone rounded-lg p-3">
              Secure booking opens in the Boulevard scheduler. Embedded in-page booking activates
              automatically once our Boulevard API access is live.
            </p>

            <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="block mt-6">
              <Button
                data-testid="booking-continue-button"
                className="w-full iri-gradient text-ink font-medium rounded-full h-12 hover:opacity-90 hover:scale-[1.02] transition-all"
              >
                Continue to booking <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </BookingContext.Provider>
  );
}

export const useBooking = () => useContext(BookingContext);
